import type { GenerateAppSchemaParameters, SchemaDefinition } from '@sap/ux-specification-types';
import { getCustomExtensionFragmentDefinitionKey } from '../../utils/macros';
import { getManifestPage, getPageCustomExtensionFile } from '../../utils/utils';
import { generateMacrosSchemaDefinition } from './generator';

type Templates = Array<string>;

/**
 * Method to populate all templates/fragments by parsing manifest extensions.
 *
 * @param manifestSections Object containing sections
 * @param templates Templates to populate
 */
function populateTemplates(manifestSections: unknown, templates: Templates): void {
    if (typeof manifestSections === 'object') {
        for (const id in manifestSections) {
            const section = manifestSections[id];
            if ('template' in section && section.template && typeof section.template === 'string') {
                templates.push(section.template);
            }
            if ('subSections' in section) {
                populateTemplates(section.subSections, templates);
            }
        }
    }
}

/**
 * Generates schema for custom extension fragments.
 *
 * @param {SchemaDefinition} appSchema - Schema of the app
 * @param {SchemaDefinition} genericMacrosSchema - generic macros schema of the app
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 * @param {string} pageId - page id from manifest targets
 * @returns the app specific JSON schema
 */
export function resolveFragments(
    appSchema: SchemaDefinition,
    genericMacrosSchema: SchemaDefinition,
    generateParameters: GenerateAppSchemaParameters,
    pageId: string
) {
    const targetManifest = getManifestPage(generateParameters.manifest, pageId);
    const templates: Templates = [];
    populateTemplates(targetManifest?.options?.settings.content?.body?.sections || {}, templates);
    for (const fragmentName of templates) {
        if (!fragmentName) {
            continue;
        }
        const definition: SchemaDefinition = {
            additionalProperties: false,
            description: 'Custom Extension Fragment',
            metadata: {},
            properties: {}
        };
        const definitionKey = getCustomExtensionFragmentDefinitionKey(fragmentName);
        appSchema['definitions'][definitionKey] = definition;
        const extensionFile = getPageCustomExtensionFile(
            generateParameters.fragments,
            targetManifest,
            generateParameters.manifest,
            'fragment',
            fragmentName
        );
        if (extensionFile) {
            // assign relative file path to project for fragment
            definition.metadata.filePath = extensionFile.relativeFilePath;
            // get fragment schema
            const { properties = {}, definitions = {} } =
                generateMacrosSchemaDefinition(
                    genericMacrosSchema,
                    extensionFile,
                    generateParameters.serviceAVT,
                    generateParameters.logger
                ) || {};
            definition.properties = properties;
            // copy schema definitions from macros schema to app schema
            for (const i in definitions) {
                if (!appSchema.definitions[i]) {
                    appSchema.definitions[i] = definitions[i];
                }
            }
        }
    }

    return genericMacrosSchema;
}
